import {
    Box,
    Button,
    CircularProgress,
    FormControlLabel,
    MenuItem,
    Paper,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { JSX, useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { parseApiError } from '../../../library/apiErrorHelper';
import { currentTenantId } from '../../../library/brandingCache';
import { fieldProps, useValidatedForm } from '../../../library/formValidation';
import { splitApiError } from '../../../library/useApiFormError';
import {
    COLLECT_TYPES,
    IAvailableProvider,
    IInvoicingSettings,
    ITestConnectionResult,
    InvoicingSettingsDraft,
    NO_PROVIDER,
    RECEIPT_COLLECT_TYPE,
    toInvoicingSettingsDraft,
    toInvoicingSettingsRequest,
} from '../../../library/models/invoicingSettings';
import ResourcesIds from '../../../resources/ResourcesIds';
import { useReactMutation, useReactQuery } from '../../../services/queryService';
import { services } from '../../../services/services';
import { useSnackbar } from '../../layout/snackbarProvider';
import { createInvoicingSchema } from './invoicingSchema';
import {
    actionsStyles,
    blockedSaveStyles,
    cardHintStyles,
    cardStyles,
    cardTitleStyles,
    credentialsRowStyles,
    fieldGridStyles,
    fullWidthFieldStyles,
    sectionStyles,
    switchHintStyles,
    switchRowStyles,
    testButtonStyles,
} from './invoicingSettings.styles';

/**
 * Where a clinic sets up how it invoices what it sells.
 *
 * Two things about this screen are load-bearing.
 *
 * **The API secret is write-only.** The response never carries it, so the field opens empty
 * whether or not one is stored, and `apiSecretSet` is the only thing that distinguishes the
 * two -- which is why the placeholder changes rather than the value. Leaving it untouched
 * sends no `apiSecret` at all, and that absence is what stops saving the VAT rate from wiping
 * the clinic's credentials.
 *
 * **Test connection is not a yes/no.** Its real job is to fill in the two fields nobody knows
 * by heart -- the fiscal code as the provider spells it, and the invoice series that actually
 * exist on the account -- so they become pickers rather than free text somebody discovers was
 * wrong when an invoice is refused. It stores nothing, so an admin can check a secret before
 * committing to it.
 *
 * react-hook-form and Yup like everything else. The rules live in `invoicingSchema`, one per
 * field: this panel has sixteen inputs and used to draw no field-level errors at all, so an
 * admin who ticked Enabled with one box blank got a greyed-out Save and no way to find out
 * which box it was.
 */
export const InvoicingSettings = (): JSX.Element => {
    const { t: translate } = useTranslation();
    const { showSnackbar } = useSnackbar();

    // Keyed by tenant, or somebody in two clinics is served the first one's settings.
    const { data, isLoading, refetch } = useReactQuery<IInvoicingSettings>(
        services.invoicing.getSettings(currentTenantId())
    );

    const saveSettings = useReactMutation(services.invoicing.saveSettingsMutation());
    const testConnection = useReactMutation(services.invoicing.testConnectionMutation());

    const schema = useMemo(() => createInvoicingSchema(translate), [translate]);

    /**
     * What the last successful test found. Kept in state rather than derived from the query,
     * because it is the answer to a question the admin asked rather than part of the record.
     */
    const [tested, setTested] = useState<ITestConnectionResult | null>(null);

    const secretIsStored: boolean = data?.apiSecretSet ?? false;
    const providers: IAvailableProvider[] = data?.availableProviders ?? [];

    const {
        handleSubmit, control, reset, setValue, setError, formState: { errors, isValid },
    } = useValidatedForm<InvoicingSettingsDraft>(schema, {
        defaultValues: toInvoicingSettingsDraft(undefined),
        // How the apiSecret rule learns that a blank field means "keep what is stored"
        // rather than "there is none".
        context: { secretIsStored },
    });

    /** Reseeded from the server, so the fields open on what is actually stored. */
    useEffect(() => {
        if (data) {
            reset(toInvoicingSettingsDraft(data));
        }
    }, [data, reset]);

    const current: InvoicingSettingsDraft = useWatch({ control }) as InvoicingSettingsDraft;
    const needsReceiptSeries: boolean = current.collectType === RECEIPT_COLLECT_TYPE;

    const knownFields: string[] = useMemo(() => Object.keys(schema.fields), [schema]);

    const update = (changes: Partial<InvoicingSettingsDraft>): void =>
        Object.entries(changes).forEach(([field, value]) =>
            setValue(field as keyof InvoicingSettingsDraft,
                value as never, { shouldValidate: true }));

    const onTest = (): void => {
        testConnection.mutate(
            {
                provider: current.provider,
                apiUser: current.apiUser,
                // Only the freshly typed one. Omitted, the API falls back to what is stored,
                // which is what makes the button useful before anything has been saved and
                // after everything has.
                apiSecret: current.apiSecret,
                companyCif: current.companyCif,
            },
            {
                onSuccess: (result: ITestConnectionResult) => {
                    setTested(result);
                    showSnackbar(translate(ResourcesIds.INVOICING_SETTINGS_TEST_SUCCESS), 'success');
                },
                onError: (error: Error) =>
                    showSnackbar(
                        parseApiError(error).message ??
                            translate(ResourcesIds.INVOICING_SETTINGS_TEST_ERROR),
                        'error'
                    ),
            }
        );
    };

    const onValid = (values: InvoicingSettingsDraft): void => {
        saveSettings.mutate(toInvoicingSettingsRequest(values), {
            onSuccess: () => {
                showSnackbar(translate(ResourcesIds.INVOICING_SETTINGS_SAVE_SUCCESS), 'success');
                refetch();
            },
            onError: (error: Error) => {
                const outcome = splitApiError(error, knownFields, translate,
                    ResourcesIds.INVOICING_SETTINGS_SAVE_ERROR);

                Object.entries(outcome.fieldErrors).forEach(([field, message]) =>
                    setError(field as keyof InvoicingSettingsDraft, { type: 'server', message }));

                if (outcome.formError) {
                    showSnackbar(outcome.formError, 'error');
                }
            },
        });
    };

    if (isLoading) {
        return (
            <Box sx={sectionStyles}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={sectionStyles}>
            <Paper sx={cardStyles} elevation={0}>
                <Typography variant="h6" sx={cardTitleStyles}>
                    {translate(ResourcesIds.INVOICING_SETTINGS_TITLE)}
                </Typography>
                <Typography variant="body2" sx={cardHintStyles}>
                    {translate(ResourcesIds.INVOICING_SETTINGS_DESCRIPTION)}
                </Typography>

                <Box sx={fieldGridStyles}>
                    <TextField
                        select
                        fullWidth
                        label={translate(ResourcesIds.INVOICING_SETTINGS_PROVIDER_LABEL)}
                        value={current.provider}
                        onChange={(event) => update({ provider: event.target.value })}
                        {...fieldProps(errors, 'provider')}
                    >
                        <MenuItem value={NO_PROVIDER}>
                            {translate(ResourcesIds.INVOICING_SETTINGS_PROVIDER_NONE)}
                        </MenuItem>
                        {providers.map((provider: IAvailableProvider) => (
                            <MenuItem key={provider.key} value={provider.key}>
                                {provider.displayName}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>

                <Box sx={switchRowStyles}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={current.enabled}
                                onChange={(_event, checked: boolean) => update({ enabled: checked })}
                            />
                        }
                        label={translate(ResourcesIds.INVOICING_SETTINGS_ENABLED_LABEL)}
                    />
                    <Typography variant="caption" component="p" sx={switchHintStyles}>
                        {translate(ResourcesIds.INVOICING_SETTINGS_ENABLED_HELPER)}
                    </Typography>
                </Box>
            </Paper>

            <Paper sx={cardStyles} elevation={0}>
                <Typography variant="h6" sx={cardTitleStyles}>
                    {translate(ResourcesIds.INVOICING_SETTINGS_CREDENTIALS_TITLE)}
                </Typography>
                <Typography variant="body2" sx={cardHintStyles}>
                    {translate(ResourcesIds.INVOICING_SETTINGS_CREDENTIALS_DESCRIPTION)}
                </Typography>

                <Box sx={fieldGridStyles}>
                    <TextField
                        fullWidth
                        label={translate(ResourcesIds.INVOICING_SETTINGS_API_USER_LABEL)}
                        value={current.apiUser}
                        onChange={(event) => update({ apiUser: event.target.value })}
                        {...fieldProps(errors, 'apiUser')}
                    />
                </Box>

                <Box sx={credentialsRowStyles}>
                    <TextField
                        fullWidth
                        type="password"
                        label={translate(ResourcesIds.INVOICING_SETTINGS_API_SECRET_LABEL)}
                        // Empty whether or not one is stored -- the API never returns it.
                        value={current.apiSecret ?? ''}
                        placeholder={translate(ResourcesIds.INVOICING_SETTINGS_API_SECRET_PLACEHOLDER)}
                        onChange={(event) => update({ apiSecret: event.target.value })}
                        // The stored/never-shown note is what this field says when there is
                        // nothing wrong, so it goes in as the hint rather than beside it.
                        {...fieldProps(errors, 'apiSecret', translate(
                            secretIsStored
                                ? ResourcesIds.INVOICING_SETTINGS_API_SECRET_STORED
                                : ResourcesIds.INVOICING_SETTINGS_API_SECRET_HELPER
                        ))}
                    />
                    <Button
                        variant="outlined"
                        sx={testButtonStyles}
                        onClick={onTest}
                        disabled={testConnection.isPending}
                    >
                        {translate(ResourcesIds.INVOICING_SETTINGS_TEST)}
                    </Button>
                </Box>
            </Paper>

            <Paper sx={cardStyles} elevation={0}>
                <Typography variant="h6" sx={cardTitleStyles}>
                    {translate(ResourcesIds.INVOICING_SETTINGS_COMPANY_TITLE)}
                </Typography>
                <Typography variant="body2" sx={cardHintStyles}>
                    {translate(ResourcesIds.INVOICING_SETTINGS_COMPANY_DESCRIPTION)}
                </Typography>

                <Box sx={fieldGridStyles}>
                    {/* A picker once the account has been read, free text before -- an admin
                        must not be blocked from typing what they know because the provider
                        has not been asked yet. */}
                    <TextField
                        select={!!tested?.companies.length}
                        fullWidth
                        label={translate(ResourcesIds.INVOICING_SETTINGS_COMPANY_CIF_LABEL)}
                        value={current.companyCif}
                        onChange={(event) => update({ companyCif: event.target.value })}
                        {...fieldProps(errors, 'companyCif')}
                    >
                        {(tested?.companies ?? []).map((company) => (
                            <MenuItem key={company.cif} value={company.cif}>
                                {company.name} ({company.cif})
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        select={!!tested?.series.length}
                        fullWidth
                        label={translate(ResourcesIds.INVOICING_SETTINGS_SERIES_LABEL)}
                        value={current.seriesName}
                        onChange={(event) => update({ seriesName: event.target.value })}
                        {...fieldProps(errors, 'seriesName',
                            tested?.series.length
                                ? undefined
                                : translate(ResourcesIds.INVOICING_SETTINGS_SERIES_HELPER))}
                    >
                        {(tested?.series ?? []).map((series: string) => (
                            <MenuItem key={series} value={series}>
                                {series}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>
            </Paper>

            <Paper sx={cardStyles} elevation={0}>
                <Typography variant="h6" sx={cardTitleStyles}>
                    {translate(ResourcesIds.INVOICING_SETTINGS_DOCUMENT_TITLE)}
                </Typography>

                <Box sx={fieldGridStyles}>
                    <TextField
                        fullWidth
                        label={translate(ResourcesIds.INVOICING_SETTINGS_VAT_NAME_LABEL)}
                        value={current.vatName}
                        onChange={(event) => update({ vatName: event.target.value })}
                    />
                    <TextField
                        fullWidth
                        // A string in the draft: coercing on every keystroke leaves a 0 that
                        // cannot be deleted.
                        label={translate(ResourcesIds.INVOICING_SETTINGS_VAT_PERCENTAGE_LABEL)}
                        value={current.vatPercentage}
                        onChange={(event) => update({ vatPercentage: event.target.value })}
                        {...fieldProps(errors, 'vatPercentage')}
                    />
                    <TextField
                        fullWidth
                        label={translate(ResourcesIds.INVOICING_SETTINGS_MEASURING_UNIT_LABEL)}
                        value={current.measuringUnit}
                        onChange={(event) => update({ measuringUnit: event.target.value })}
                    />
                    <TextField
                        fullWidth
                        label={translate(ResourcesIds.INVOICING_SETTINGS_PRODUCT_TYPE_LABEL)}
                        value={current.productType}
                        onChange={(event) => update({ productType: event.target.value })}
                    />
                    <TextField
                        fullWidth
                        label={translate(ResourcesIds.INVOICING_SETTINGS_LANGUAGE_LABEL)}
                        value={current.language}
                        onChange={(event) => update({ language: event.target.value })}
                    />
                    <TextField
                        fullWidth
                        label={translate(ResourcesIds.INVOICING_SETTINGS_CURRENCY_LABEL)}
                        value={current.currency}
                        onChange={(event) => update({ currency: event.target.value })}
                    />
                </Box>

                <Box sx={switchRowStyles}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={current.pricesIncludeVat}
                                onChange={(_event, checked: boolean) =>
                                    update({ pricesIncludeVat: checked })
                                }
                            />
                        }
                        label={translate(ResourcesIds.INVOICING_SETTINGS_PRICES_INCLUDE_VAT_LABEL)}
                    />
                    <Typography variant="caption" component="p" sx={switchHintStyles}>
                        {translate(ResourcesIds.INVOICING_SETTINGS_PRICES_INCLUDE_VAT_HELPER)}
                    </Typography>
                </Box>
            </Paper>

            <Paper sx={cardStyles} elevation={0}>
                <Typography variant="h6" sx={cardTitleStyles}>
                    {translate(ResourcesIds.INVOICING_SETTINGS_PAYMENT_TITLE)}
                </Typography>
                <Typography variant="body2" sx={cardHintStyles}>
                    {translate(ResourcesIds.INVOICING_SETTINGS_PAYMENT_DESCRIPTION)}
                </Typography>

                <Box sx={fieldGridStyles}>
                    <TextField
                        select
                        fullWidth
                        label={translate(ResourcesIds.INVOICING_SETTINGS_COLLECT_TYPE_LABEL)}
                        value={current.collectType}
                        onChange={(event) => update({ collectType: event.target.value })}
                    >
                        {COLLECT_TYPES.map((type: string) => (
                            <MenuItem key={type} value={type}>
                                {type}
                            </MenuItem>
                        ))}
                    </TextField>

                    {/* Only the one type the provider numbers itself needs a series, so the
                        field appears with it rather than sitting empty and unexplained. */}
                    {needsReceiptSeries && (
                        <TextField
                            fullWidth
                            label={translate(ResourcesIds.INVOICING_SETTINGS_COLLECT_SERIES_LABEL)}
                            value={current.collectSeriesName}
                            onChange={(event) => update({ collectSeriesName: event.target.value })}
                            {...fieldProps(errors, 'collectSeriesName', translate(
                                ResourcesIds.INVOICING_SETTINGS_COLLECT_SERIES_HELPER
                            ))}
                            sx={fullWidthFieldStyles}
                        />
                    )}
                </Box>

                <Box sx={switchRowStyles}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={current.sendEmailToClient}
                                onChange={(_event, checked: boolean) =>
                                    update({ sendEmailToClient: checked })
                                }
                            />
                        }
                        label={translate(ResourcesIds.INVOICING_SETTINGS_SEND_EMAIL_LABEL)}
                    />
                    <Typography variant="caption" component="p" sx={switchHintStyles}>
                        {translate(ResourcesIds.INVOICING_SETTINGS_SEND_EMAIL_HELPER)}
                    </Typography>
                </Box>
            </Paper>

            <Box sx={actionsStyles}>
                {/* Beside a disabled Save, never instead of one. */}
                {!isValid && (
                    <Typography variant="body2" sx={blockedSaveStyles}>
                        {translate(ResourcesIds.FORM_BLOCKED_SAVE)}
                    </Typography>
                )}
                <Button
                    variant="contained"
                    onClick={handleSubmit(onValid)}
                    disabled={!isValid || saveSettings.isPending}
                >
                    {translate(ResourcesIds.INVOICING_SETTINGS_SAVE)}
                </Button>
            </Box>
        </Box>
    );
};
